import React, { useState, useEffect } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Hero = () => {
  const [desktopIndex, setDesktopIndex] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Banner sources
  const [desktopBannersState, setDesktopBannersState] = useState([]);
  const [mobileBannersState, setMobileBannersState] = useState([]);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  
  const handleUserInteraction = () => {
    setIsUserInteracting(true);

    setTimeout(() => {
      setIsUserInteracting(false);
    }, 5000); // resume after 5s idle
  };

  useEffect(() => {
    if (!desktopBannersState.length || isUserInteracting) return;

    const interval = setInterval(() => {
      handleSlideChange(() => {
        setDesktopIndex((prev) =>
          desktopBannersState.length ? (prev + 1) % desktopBannersState.length : 0
        );
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [desktopBannersState.length, isUserInteracting]);
  
  useEffect(() => {
    if (!mobileBannersState.length || isUserInteracting) return;

    const interval = setInterval(() => {
      handleSlideChange(() => {
        setMobileIndex((prev) =>
          mobileBannersState.length ? (prev + 1) % mobileBannersState.length : 0
        );
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [mobileBannersState.length, isUserInteracting]);
  
  // Handle transitions smoothly
  const handleSlideChange = (callback) => {
    setIsTransitioning(true);
    callback();
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const handleMouseEnter = () => {
    setIsUserInteracting(true);
  };

  const handleMouseLeave = () => {
    setTimeout(() => {
      setIsUserInteracting(false);
    }, 2000); // small delay before resume
  };

  useEffect(() => {
    fetchBanner();
  }, []);

  const fetchBanner = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/banner/get`
      );
      const data = res.data?.data || [];

      // Map banners to objects with src and isVideo
      const desktop = data
        .filter((b) => b.screen === "desktop" || !b.screen)
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
        .map((b) => ({
          src: b.video || b.image || b.imageUrl,
          isVideo: Boolean(b.video),
          link: b.link || "",
        }))
        .filter((item) => Boolean(item.src));
      const mobile = data
        .filter((b) => b.screen === "mobile")
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
        .map((b) => ({
          src: b.video || b.image || b.imageUrl,
          isVideo: Boolean(b.video),
          link: b.link || "",
        }))
        .filter((item) => Boolean(item.src));

      if (desktop.length) {
        setDesktopBannersState(desktop);
        setDesktopIndex(0);
      }
      if (mobile.length) {
        setMobileBannersState(mobile);
        setMobileIndex(0);
      }
    } catch (error) {
      console.error("banner fetch error--->", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBannerClick = (link) => {
    if (!link) return;

    if (link.startsWith("#")) {
      setTimeout(() => {
        const el = document.querySelector(link);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        } else {
          console.warn("Element not found:", link);
        }
      }, 100); // small delay ensures DOM is ready
      return;
    }

    if (link.startsWith("http")) {
      window.open(link, "_blank");
      return;
    }

    window.location.href = link;
  };

  // Slider controls
  const prevMobile = () => {
    if (!mobileBannersState.length || isTransitioning) return;
    handleUserInteraction();
    handleSlideChange(() => {
      setMobileIndex(
        (prev) =>
          (prev - 1 + mobileBannersState.length) % mobileBannersState.length
      );
    });
  };
  const nextMobile = () => {
    if (!mobileBannersState.length || isTransitioning) return;
    handleUserInteraction();
    handleSlideChange(() => {
      setMobileIndex((prev) => (prev + 1) % mobileBannersState.length);
    });
  };

  const prevDesktop = () => {
    if (!desktopBannersState.length || isTransitioning) return;
    handleUserInteraction();
    handleSlideChange(() => {
      setDesktopIndex(
        (prev) =>
          (prev - 1 + desktopBannersState.length) % desktopBannersState.length
      );
    });
  };
  const nextDesktop = () => {
    if (!desktopBannersState.length || isTransitioning) return;
    handleUserInteraction();
    handleSlideChange(() => {
      setDesktopIndex((prev) => (prev + 1) % desktopBannersState.length);
    });
  };

  return (
    <>
      {/* MOBILE VIEW */}
      <div className="md:hidden bg-gradient-to-b from-[#FFFAF9] to-white overflow-hidden relative">
        {loading ? (
          <div className="w-full h-64 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse">
            <div className="h-full flex items-center justify-center">
              <div className="w-12 h-12 border-4 border-gray-300 border-t-[#ab310c] rounded-full animate-spin"></div>
            </div>
          </div>
        ) : mobileBannersState.length ? (
          <>
              <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className="relative overflow-hidden shadow-lg">
              <div
                className="flex transition-all duration-700 ease-out"
                style={{
                  transform: `translateX(-${mobileIndex * 100}%)`,
                  opacity: isTransitioning ? 0.9 : 1
                }}
              >
                {mobileBannersState.map((bannerItem, index) => (
                  <div
                    key={index}
                    onClick={() => handleBannerClick(bannerItem.link)}
                    className={`w-full flex-shrink-0 relative ${bannerItem.link ? "cursor-pointer hover:scale-[1.01]" : "cursor-default"
                      } transition-transform duration-300`}
                  >
                    {bannerItem.isVideo ? (
                      <video
                        onClick={(e) => e.stopPropagation()}
                        src={bannerItem.src}
                        className="w-full h-auto object-cover"
                        controls
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={bannerItem.src}
                        alt={`Mobile Banner ${index + 1}`}
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                    )}
                    {/* Gradient overlay for better text readability */}
                    <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                ))}
              </div>

              {/* Arrows with hover effects */}
              <button
                aria-label="Previous mobile banner"
                onClick={prevMobile}
                disabled={loading || !mobileBannersState.length || isTransitioning}
                className="absolute left-4 top-1/2 opacity-60 hover:opacity-90 -translate-y-1/2 bg-white/90 p-2.5 rounded-full shadow-lg hover:bg-white hover:scale-110 hover:shadow-xl active:scale-95 transition-all duration-200 flex items-center justify-center group"
              >
                <ChevronLeft className="text-gray-700 group-hover:text-[#ab310c] transition-colors" size={20} />
              </button>
              <button
                aria-label="Next mobile banner"
                onClick={nextMobile}
                disabled={loading || !mobileBannersState.length || isTransitioning}
                className="absolute right-4 top-1/2 opacity-60 hover:opacity-90 -translate-y-1/2 bg-white/90 p-2.5 rounded-full shadow-lg hover:bg-white hover:scale-110 hover:shadow-xl active:scale-95 transition-all duration-200 flex items-center justify-center group"
              >
                <ChevronRight className="text-gray-700 group-hover:text-[#ab310c] transition-colors" size={20} />
              </button>

              {/* Dots with animation */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 items-center justify-center backdrop-blur-sm bg-white/30 p-2 rounded-full">
                {mobileBannersState.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      if (!isTransitioning) {
                        handleUserInteraction()
                        handleSlideChange(() => setMobileIndex(i));
                      }
                    }}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`h-2 w-2 rounded-full transition-all duration-300 transform ${i === mobileIndex
                      ? "bg-[#ab310c] scale-125 w-6"
                      : "bg-white/80 hover:bg-white"
                      }`}
                  />
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-64 flex flex-col items-center justify-center text-center space-y-4 p-4">
            <div className="relative">
              <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-12 h-12 text-gray-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 7a4 4 0 014-4h10a4 4 0 014 4v10a4 4 0 01-4 4H7a4 4 0 01-4-4V7z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 13l2-2 4 4 3-3 3 3"
                  />
                </svg>
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#ab310c]/10 rounded-full flex items-center justify-center">
                <span className="text-[#ab310c] text-sm">!</span>
              </div>
            </div>
            <div>
              <div className="text-xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent">
                No banners yet
              </div>
              <div className="text-sm text-gray-500 max-w-xs mt-2">
                Mobile banners will appear here once uploaded
              </div>
            </div>
          </div>
        )}
      </div>

      {/* DESKTOP VIEW */}
      <div className="hidden md:block bg-gradient-to-b from-[#FFFAF9] to-white h-auto 4xl:h-[90vh] relative overflow-hidden">
        {loading ? (
          <div className="w-full h-[40vh] md:h-[50vh] lg:h-[60vh] 4xl:h-[90vh] bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse">
            <div className="h-full flex items-center justify-center">
              <div className="w-16 h-16 border-4 border-gray-300 border-t-[#ab310c] rounded-full animate-spin"></div>
            </div>
          </div>
        ) : desktopBannersState.length ? (
            <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="relative h-full">
            <div className="overflow-hidden shadow-2xl h-full">
              <div
                className="flex h-full transition-all duration-700 ease-out"
                style={{
                  transform: `translateX(-${desktopIndex * 100}%)`,
                  opacity: isTransitioning ? 0.95 : 1
                }}
              >
                {desktopBannersState.map((bannerItem, index) => (
                  <div
                    key={index}
                    onClick={() => handleBannerClick(bannerItem.link)}
                    className={`w-full h-full flex-shrink-0 relative ${bannerItem.link ? "cursor-pointer hover:scale-[1.01]" : "cursor-default"
                      } transition-transform duration-300`}
                  >
                    {bannerItem.isVideo ? (
                      <video
                        src={bannerItem.src}
                        className="w-full h-[650px] object-cover"
                        autoPlay
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={bannerItem.src}
                        alt={`Desktop Banner ${index + 1}`}
                        className="w-full h-[650px] object-cover"
                        loading="lazy"
                      />
                    )}
                    {/* Enhanced gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Arrows with enhanced styling */}
            <button
              aria-label="Previous desktop banner"
              onClick={prevDesktop}
              disabled={loading || !desktopBannersState.length || isTransitioning}
              className="absolute left-8 top-1/2 opacity-60 hover:opacity-90 -translate-y-1/2 bg-white/95 p-4 rounded-full shadow-2xl hover:bg-white hover:scale-110 hover:shadow-3xl active:scale-95 transition-all duration-300 flex items-center justify-center group backdrop-blur-sm"
            >
              <ChevronLeft className="text-gray-800 group-hover:text-[#ab310c] transition-colors" size={24} />
              <span className="sr-only">Previous</span>
            </button>
            <button
              aria-label="Next desktop banner"
              onClick={nextDesktop}
              disabled={loading || !desktopBannersState.length || isTransitioning}
              className="absolute right-8 top-1/2 opacity-60 hover:opacity-90 -translate-y-1/2 bg-white/95 p-4 rounded-full shadow-2xl hover:bg-white hover:scale-110 hover:shadow-3xl active:scale-95 transition-all duration-300 flex items-center justify-center group backdrop-blur-sm"
            >
              <ChevronRight className="text-gray-800 group-hover:text-[#ab310c] transition-colors" size={24} />
              <span className="sr-only">Next</span>
            </button>

            {/* Enhanced dots indicator */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3 items-center justify-center backdrop-blur-md bg-white/40 p-3 rounded-full shadow-lg">
              {desktopBannersState.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    if (!isTransitioning) {
                      handleSlideChange(() => setDesktopIndex(i));
                    }
                  }}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-2.5 rounded-full transition-all duration-500 transform hover:scale-125 ${i === desktopIndex
                    ? "bg-[#ab310c] w-8 shadow-md"
                    : "bg-white/90 w-2.5 hover:bg-white"
                    }`}
                />
              ))}
              <div className="text-xs font-medium text-gray-700 ml-2 px-2 py-1 bg-white/90 rounded-full">
                {desktopIndex + 1} / {desktopBannersState.length}
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-[40vh] md:h-[50vh] lg:h-[60vh] 4xl:h-[90vh] flex flex-col items-center justify-center p-8">
            <div className="max-w-2xl text-center space-y-6">
              <div className="inline-block p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-xl">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-20 h-20 text-gray-300 mx-auto"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  No banners available
                </h3>
                <p className="text-gray-500 mt-2 max-w-md mx-auto">
                  Add desktop banners to showcase your content in a beautiful slideshow
                </p>
              </div>
              <button
                onClick={fetchBanner}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#ab310c] text-white rounded-full hover:bg-[#8a290a] transition-colors duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
                Refresh Banners
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Hero;